// =============================================================================
// POS táctil — pensado para tablet/touchscreen en una caja de ferretería.
// Premium (no un campo de pistola): grilla de productos por categoría, búsqueda,
// teclado numérico, selector de cliente, fiado, factura electrónica, soporte
// pistola HID (escribe en el buscador). Offline-first (Dexie + sync idempotente).
// =============================================================================
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { catalog as catalogApi, crm as crmApi, pos as posApi, ApiError, type CatalogItem, type Customer } from "../lib/api";
import { supabase } from "../lib/supabase";
import { db } from "../lib/offline/db";
import { syncQueue } from "../lib/offline/sync";
import { refreshCatalogCache } from "../lib/offline/catalog-cache";
import { money, uuid, itbmsPct, fiscalRound } from "../lib/utils";
import { useActiveBranch } from "../lib/session";
import { CheckoutPanel, type CheckoutResult } from "../components/CheckoutPanel";
import { CashBar, useCashSession } from "../components/CashBar";
import { NumericKeypad } from "../components/NumericKeypad";
import { NewCustomerSheet } from "../components/CustomerPicker";
import { CustomerCombobox } from "../components/CustomerCombobox";
import { PostSaleModal, type PostSaleData } from "../components/PostSaleModal";
import { printReceipt, type PrinterConfig } from "../lib/hardware/printer";
import { composeReceipt } from "../lib/hardware/receipt";

const PRINTER: PrinterConfig = { transport: "network", target: "192.168.1.50:9100" };

export const Route = createFileRoute("/pos")({ component: Pos });

interface CartLine {
  clientUuid: string;
  productUnitId: string;
  productId: string;
  name: string;
  unitLabel: string;       // "saco", "und", "m", etc.
  qty: number;
  unitPrice: number;
  discount: number;        // B/. absoluto, descuento por línea
  itbmsRate: string;
  allowsFraction: boolean;
  lineNote?: string;       // descripción libre cuando es ítem libre
}

function Pos() {
  const BRANCH = useActiveBranch();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCustomer, setShowCustomer] = useState(false);
  const [keypad, setKeypad] = useState<null | { lineId: string; mode: "qty" | "discount"; max?: number; allowFraction: boolean }>(null);
  const [unitPicker, setUnitPicker] = useState<CatalogItem | null>(null);
  const [showPark, setShowPark] = useState(false);                 // modal "suspender"
  const [showQuote, setShowQuote] = useState(false);               // modal "cotizar"
  const [showResume, setShowResume] = useState(false);             // modal "retomar"
  const [showOpenItem, setShowOpenItem] = useState(false);         // modal "ítem libre"
  const [globalDiscount, setGlobalDiscount] = useState(0);          // B/. absoluto sobre el subtotal
  const [postSale, setPostSale] = useState<(PostSaleData & { receiptContent: string; defaultEmail?: string }) | null>(null);
  const { data: session } = useCashSession(BRANCH);

  // ---- catálogo: categorías + grilla ----
  const { data: categories = [] } = useQuery({
    queryKey: ["pos-categories"],
    queryFn: () => crmApi.listCategories(),
    staleTime: 5 * 60_000,
  });
  // Lista de precios efectiva: la del cliente si tiene una asignada; si no, la default.
  const priceListId = customer?.price_list_id ?? null;
  const { data: products = [], isLoading: loadingGrid } = useQuery<CatalogItem[]>({
    queryKey: ["pos-grid", BRANCH, categoryId, search, priceListId],
    queryFn: () => catalogApi.list(BRANCH, search || undefined, 100, 0, categoryId, true, priceListId),
    enabled: !!BRANCH,
  });

  useEffect(() => { void refreshCatalogCache(BRANCH); }, [BRANCH]);

  // ---- totales (espejo del servidor: itbms_pct + fiscal_round) ----
  // Cada línea: base = unit_price*qty − discount_línea − cuota_global_prorrateada.
  // Global se prorratea proporcional al pre-base (unit_price*qty) para que ITBMS
  // baje en cada línea (cumple DGI §8.4.1: base imponible reducida por línea).
  const basePre = cart.reduce((s, l) => s + l.unitPrice * l.qty, 0);
  const globalApplied = Math.min(globalDiscount, basePre);
  const lineTotals = cart.map((l) => {
    const pre = l.unitPrice * l.qty;
    const proRata = basePre > 0 ? fiscalRound(globalApplied * (pre / basePre)) : 0;
    const lineDisc = (l.discount ?? 0) + proRata;
    const base = Math.max(fiscalRound(pre - lineDisc), 0);
    const itbms = fiscalRound(base * itbmsPct(l.itbmsRate));
    return { base, itbms, total: fiscalRound(base + itbms), proRata, lineDisc };
  });
  const subtotal = lineTotals.reduce((s, l) => s + l.base, 0);
  const itbmsTotal = lineTotals.reduce((s, l) => s + l.itbms, 0);
  const totalDiscount = cart.reduce((s, l) => s + (l.discount ?? 0), 0) + globalApplied;
  const total = fiscalRound(subtotal + itbmsTotal);

  // ---- pistola HID: el lector tipea en el buscador + Enter ----
  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const code = search.trim();
    if (!code) return;
    // Si parece código de barras (numérico ≥8 dígitos), resuelve por barcode.
    if (/^\d{8,}$/.test(code)) {
      void (async () => {
        try {
          const r = await posApi.resolveBarcode(BRANCH, code);
          addProductByUnit({
            productId: r.product_id, productUnitId: r.product_unit_id, name: r.name,
            itbmsRate: r.itbms_rate, unitPrice: r.price ?? 0, qty: 1,
            unitLabel: r.factor === 1 ? "und" : "x"+r.factor, allowsFraction: false,
          });
          setSearch("");
          setMsg(null);
        } catch (e) {
          setMsg(`Código ${code} no encontrado`);
        }
      })();
    }
    // Si es texto, queryFn ya reactivó la grilla.
  }

  /** Si el producto tiene >1 presentación vendible, abre el picker; sin args. */
  function onTapProduct(p: CatalogItem) {
    if (p.units.length > 1) { setUnitPicker(p); return; }
    addProduct(p, 0);
  }
  function addProduct(p: CatalogItem, unitIndex: number) {
    const u = p.units[unitIndex];
    if (!u) { setMsg(`${p.name} no tiene presentación vendible`); return; }
    if (u.price == null) { setMsg(`${p.name} no tiene precio configurado`); return; }
    addProductByUnit({
      productId: p.id, productUnitId: u.product_unit_id, name: p.name,
      itbmsRate: p.itbms_rate, unitPrice: u.price, qty: 1,
      unitLabel: u.factor === 1 ? u.unit_code : `${u.unit_code} (×${u.factor})`,
      allowsFraction: false,
    });
  }

  function addProductByUnit(p: { productId: string; productUnitId: string; name: string; itbmsRate: string; unitPrice: number; qty: number; unitLabel: string; allowsFraction: boolean; lineNote?: string }) {
    setCart((c) => {
      // Ítems libres NUNCA se agrupan (cada uno es único; agrupar borraría su nota).
      if (!p.lineNote) {
        const i = c.findIndex((l) => l.productUnitId === p.productUnitId && !l.lineNote);
        if (i >= 0) { const cp = [...c]; cp[i] = { ...cp[i], qty: cp[i].qty + p.qty }; return cp; }
      }
      return [...c, { clientUuid: uuid(), discount: 0, ...p }];
    });
  }

  // Ítem libre: el cajero teclea descripción + precio + qty + ITBMS.
  // Se mapea a la línea con productUnitId del producto especial OPEN-ITEM.
  async function addOpenItem(args: { description: string; unitPrice: number; qty: number; itbmsRate: string }) {
    try {
      const openPuId = await posApi.ensureOpenItem();
      addProductByUnit({
        productId: "open-item", productUnitId: openPuId,
        name: args.description, unitLabel: "und",
        qty: args.qty, unitPrice: args.unitPrice,
        itbmsRate: args.itbmsRate, allowsFraction: true,
        lineNote: args.description,
      });
      setShowOpenItem(false);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  }

  function setLineQty(lineId: string, qty: number) {
    if (qty <= 0) return removeLine(lineId);
    setCart((c) => c.map((l) => l.clientUuid === lineId ? { ...l, qty } : l));
  }
  function setLineDiscount(lineId: string, discount: number) {
    setCart((c) => c.map((l) => {
      if (l.clientUuid !== lineId) return l;
      const max = l.unitPrice * l.qty;
      const safe = Math.max(0, Math.min(discount, max));
      return { ...l, discount: safe };
    }));
  }
  function removeLine(lineId: string) { setCart((c) => c.filter((l) => l.clientUuid !== lineId)); }

  function resetCart() {
    setCart([]); setCustomer(null); setGlobalDiscount(0);
  }

  async function onCheckoutConfirm(r: CheckoutResult) {
    const clientUuid = uuid();
    await db.queuedSales.add({
      clientUuid, branchId: BRANCH, cashSessionId: session?.id ?? null,
      customerId: r.customerId ?? customer?.id ?? null,
      lines: cart.map((l, i) => ({
        clientUuid: l.clientUuid, productUnitId: l.productUnitId, productId: l.productId,
        qty: l.qty, unitPrice: l.unitPrice, name: l.name,
        discount: fiscalRound((l.discount ?? 0) + (lineTotals[i]?.proRata ?? 0)),
        lineNote: l.lineNote,
      })),
      payments: r.payments.map((p) => ({ method: p.method, amount: p.amount })),
      changeGiven: r.changeGiven, total, emitInvoice: r.emitInvoice,
      createdAt: Date.now(), status: "pending", attempts: 0,
    });
    // Tiquete pre-armado: el cajero puede imprimir o enviar por correo desde el
    // modal post-sale (patrón Alegra: ✓ guardada, total, vuelto, enviar/imprimir).
    const receiptContent = composeReceipt({
      org: "Ferretería El Tornillo", branch: "Caja",
      items: cart.map((l) => ({ name: l.name, qty: l.qty, unitPrice: l.unitPrice })),
      total, paid: r.payments.reduce((s, p) => s + p.amount, 0), change: r.changeGiven,
      method: r.payments.map((p) => p.method).join("+"),
    });
    setPostSale({
      total, change: r.changeGiven, receiptContent,
      defaultEmail: customer?.email ?? undefined,
    });
    setShowCheckout(false);
    void syncQueue();
  }

  // ---------------------------------------------------------------------------
  // Suspender venta: persiste en server (cross-caja) + limpia carrito local.
  // Requiere online; offline guarda como queued sale con status especial.
  // ---------------------------------------------------------------------------
  async function parkSale(label: string) {
    if (cart.length === 0) return;
    if (!navigator.onLine) { setMsg("Suspender requiere conexión"); return; }
    try {
      const clientUuid = uuid();
      const remote = await posApi.openSale(BRANCH, clientUuid, {
        cashSessionId: session?.id ?? undefined,
        customerId: customer?.id ?? undefined,
      });
      for (const [i, l] of cart.entries()) {
        const proRata = lineTotals[i]?.proRata ?? 0;
        const finalDisc = fiscalRound((l.discount ?? 0) + proRata);
        await posApi.addLine(remote.id, l.productUnitId, l.qty, l.unitPrice, finalDisc, l.clientUuid);
      }
      await posApi.parkSale(remote.id, label);
      resetCart();
      setShowPark(false);
      setMsg(`Venta suspendida (${label}). La retomas en "↪ Retomar".`);
      void parkedQuery.refetch();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function quoteSale(label: string, validDays: number) {
    if (cart.length === 0) return;
    if (!navigator.onLine) { setMsg("Cotizar requiere conexión"); return; }
    try {
      const clientUuid = uuid();
      const remote = await posApi.openSale(BRANCH, clientUuid, {
        cashSessionId: session?.id ?? undefined,
        customerId: customer?.id ?? undefined,
      });
      for (const [i, l] of cart.entries()) {
        const proRata = lineTotals[i]?.proRata ?? 0;
        const finalDisc = fiscalRound((l.discount ?? 0) + proRata);
        await posApi.addLine(remote.id, l.productUnitId, l.qty, l.unitPrice, finalDisc, l.clientUuid, l.lineNote);
      }
      await posApi.quoteSale(remote.id, label, validDays);
      resetCart();
      setShowQuote(false);
      setMsg(`Cotización guardada (${label}, válida ${validDays} días). La retomas en "↪ Retomar".`);
      void parkedQuery.refetch();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function resumeSale(saleId: string) {
    if (!navigator.onLine) { setMsg("Retomar requiere conexión"); return; }
    try {
      await posApi.resumeSale(saleId);
      // 1) líneas de la venta retomada
      const linesRes = await supabase.from("sale_lines")
        .select("id,product_id,product_unit_id,qty,unit_price,discount,itbms_rate")
        .eq("sale_id", saleId);
      if (linesRes.error) throw new ApiError(linesRes.error.message, "fetch-lines");
      type LineRow = { id: string; product_id: string; product_unit_id: string; qty: number; unit_price: number; discount: number; itbms_rate: string };
      const rows = (linesRes.data ?? []) as unknown as LineRow[];
      if (rows.length === 0) {
        setMsg("La venta no tenía líneas.");
        setShowResume(false); void parkedQuery.refetch(); return;
      }
      // 2) nombre + unidad: si está en el grid actual úsalo; si no, fetch directo.
      const missing = rows.filter((r) => !products.find((p) => p.id === r.product_id)).map((r) => r.product_id);
      type Meta = { id: string; name: string; itbms_rate: string; unit_code: string };
      const metaById = new Map<string, Meta>();
      for (const p of products) {
        const u = p.units.find((x) => x.product_unit_id === rows.find((r) => r.product_id === p.id)?.product_unit_id);
        metaById.set(p.id, { id: p.id, name: p.name, itbms_rate: p.itbms_rate, unit_code: u?.unit_code ?? "und" });
      }
      if (missing.length > 0) {
        const extra = await supabase.from("products")
          .select("id,name,itbms_rate,product_units(id,unit_id,units_of_measure(code))")
          .in("id", missing);
        type ProdRow = { id: string; name: string; itbms_rate: string; product_units?: { id: string; units_of_measure?: { code: string } }[] };
        for (const p of (extra.data ?? []) as unknown as ProdRow[]) {
          const pu = p.product_units?.find((x) => x.id === rows.find((r) => r.product_id === p.id)?.product_unit_id);
          metaById.set(p.id, { id: p.id, name: p.name, itbms_rate: p.itbms_rate, unit_code: pu?.units_of_measure?.code ?? "und" });
        }
      }
      const restored: CartLine[] = rows.map((r) => {
        const m = metaById.get(r.product_id);
        return {
          clientUuid: uuid(),
          productUnitId: r.product_unit_id, productId: r.product_id,
          name: m?.name ?? "(producto)", unitLabel: m?.unit_code ?? "und",
          qty: Number(r.qty), unitPrice: Number(r.unit_price),
          discount: Number(r.discount ?? 0),
          itbmsRate: (m?.itbms_rate ?? r.itbms_rate ?? "rate_7"),
          allowsFraction: false,
        };
      });
      setCart(restored);
      setShowResume(false);
      setMsg(`Venta retomada (${restored.length} líneas). Termina de cobrarla.`);
      void parkedQuery.refetch();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  }

  // Lista de ventas suspendidas / cotizaciones de esta sucursal (cross-caja).
  const parkedQuery = useQuery({
    queryKey: ["parked-sales", BRANCH],
    queryFn: () => posApi.listParked(BRANCH),
    enabled: !!BRANCH,
    refetchInterval: 30_000,
  });
  const parkedCount = parkedQuery.data?.length ?? 0;

  return (
    <div>
      <CashBar branchId={BRANCH} session={session ?? null} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_380px]">
        {/* ===== Izquierda: búsqueda + categorías + grilla ===== */}
        <div className="flex min-w-0 flex-col">
          <form onSubmit={onSearchSubmit} className="mb-3 flex gap-2">
            <input
              autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="🔎 Buscar o pistolear código…"
              className="flex-1 rounded-xl border px-4 py-3 text-base outline-none focus:border-brand"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="btn btn-ghost">✕</button>
            )}
            <button type="button" onClick={() => setShowOpenItem(true)}
              className="whitespace-nowrap rounded-xl border border-grafito-300 bg-white px-4 py-3 text-sm font-semibold text-grafito-700 hover:bg-grafito-50"
              title="Vender algo que no está en catálogo (servicio, ajuste, etc.)">
              + Ítem libre
            </button>
          </form>

          {/* tabs categorías */}
          <div className="mb-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <Chip active={categoryId === null} onClick={() => setCategoryId(null)}>
              <span aria-hidden>📦</span> Todo
            </Chip>
            {categories.map((c) => (
              <Chip key={c.id} active={categoryId === c.id} onClick={() => setCategoryId(c.id)}>
                <span aria-hidden>{iconFor(c.name)}</span> {c.name}
              </Chip>
            ))}
          </div>

          {msg && <div className="mb-3 rounded-lg bg-grafito-100 px-4 py-2 text-sm">{msg}</div>}

          {/* grilla productos */}
          {loadingGrid ? (
            <div className="rounded-xl border bg-white p-8 text-center text-grafito-400">Cargando productos…</div>
          ) : products.length === 0 ? (
            <div className="rounded-xl border bg-white p-8 text-center text-grafito-400">
              No hay productos en {categoryId ? "esta categoría" : "el catálogo"}.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {products.map((p) => <ProductTile key={p.id} p={p} onTap={() => onTapProduct(p)} />)}
            </div>
          )}
        </div>

        {/* ===== Derecha: cliente + carrito + totales + cobrar ===== */}
        <aside className="flex flex-col gap-3 lg:sticky lg:top-3 lg:max-h-[calc(100vh-1.5rem)]">
          {/* cliente inline (combobox + Nuevo) — patrón Alegra */}
          <div>
            <CustomerCombobox value={customer}
              onPick={(c) => setCustomer(c)}
              onNew={() => setShowCustomer(true)} />
            {priceListId && (
              <div className="mt-1 inline-block rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
                Lista de precios personalizada
              </div>
            )}
          </div>

          {/* líneas */}
          <div className="flex flex-1 flex-col rounded-xl border bg-white">
            <div className="border-b px-4 py-2 text-xs font-semibold uppercase text-grafito-400">
              Carrito ({cart.length})
            </div>
            <div className="flex-1 overflow-y-auto">
              {cart.length === 0 ? (
                <div className="p-6 text-center text-sm text-grafito-400">Toca un producto para agregarlo.</div>
              ) : cart.map((l, i) => (
                <CartLineRow key={l.clientUuid} line={l} computedTotal={lineTotals[i]?.total ?? 0}
                  onInc={() => setLineQty(l.clientUuid, l.qty + 1)}
                  onDec={() => setLineQty(l.clientUuid, l.qty - 1)}
                  onEditQty={() => setKeypad({ lineId: l.clientUuid, mode: "qty", allowFraction: l.allowsFraction })}
                  onEditDiscount={() => setKeypad({ lineId: l.clientUuid, mode: "discount", allowFraction: true, max: l.unitPrice * l.qty })}
                  onRemove={() => removeLine(l.clientUuid)}
                />
              ))}
            </div>

            {/* totales + descuento global */}
            <div className="border-t p-4">
              {/* Descuento global: el cajero teclea cuánto bajarle al total. */}
              <div className="mb-2 flex items-center gap-2 text-sm">
                <span className="text-grafito-400">Desc. global</span>
                <input
                  type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00"
                  value={globalDiscount || ""}
                  onChange={(e) => setGlobalDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="ml-auto w-24 rounded-lg border px-2 py-1 text-right outline-none focus:border-brand"
                  disabled={cart.length === 0}
                />
                {globalDiscount > 0 && <button onClick={() => setGlobalDiscount(0)} className="text-xs text-grafito-400">✕</button>}
              </div>
              <Row label="Subtotal" value={money(subtotal)} muted />
              {totalDiscount > 0 && <Row label={`Descuento`} value={`− ${money(totalDiscount)}`} muted />}
              <Row label="ITBMS" value={money(itbmsTotal)} muted />
              <div className="my-2 border-t" />
              <Row label="Total" value={money(total)} big />
              <button onClick={() => setShowCheckout(true)} disabled={cart.length === 0}
                className="btn btn-primary mt-3 w-full py-3 text-lg disabled:opacity-40">
                Cobrar
              </button>
              {/* Secundarios: suspender · cotizar · retomar */}
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button onClick={() => setShowPark(true)} disabled={cart.length === 0}
                  className="rounded-lg border border-grafito-200 bg-white py-2 text-xs font-semibold text-grafito-700 hover:bg-grafito-50 disabled:opacity-40"
                  title="Cliente fue a buscar plata">
                  ⏸ Suspender
                </button>
                <button onClick={() => setShowQuote(true)} disabled={cart.length === 0}
                  className="rounded-lg border border-grafito-200 bg-white py-2 text-xs font-semibold text-grafito-700 hover:bg-grafito-50 disabled:opacity-40"
                  title="Cotización formal (15 días); no descuenta stock">
                  📋 Cotizar
                </button>
                <button onClick={() => setShowResume(true)}
                  className="relative rounded-lg border border-grafito-200 bg-white py-2 text-xs font-semibold text-grafito-700 hover:bg-grafito-50">
                  ↪ Retomar
                  {parkedCount > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">{parkedCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showCheckout && (
        <CheckoutPanel
          total={total} online={navigator.onLine} hasCashSession={!!session}
          customer={customer}
          onPickCustomer={() => setShowCustomer(true)}
          onConfirm={onCheckoutConfirm} onCancel={() => setShowCheckout(false)}
        />
      )}

      {showCustomer && (
        <NewCustomerSheet
          onCreated={(c) => { setCustomer(c); setShowCustomer(false); }}
          onCancel={() => setShowCustomer(false)}
        />
      )}

      {keypad && (() => {
        const l = cart.find((x) => x.clientUuid === keypad.lineId);
        if (!l) return null;
        if (keypad.mode === "discount") {
          return (
            <NumericKeypad
              title={`Descuento B/. — ${l.name} (máx ${money(l.unitPrice * l.qty)})`}
              initial={String(l.discount || 0)} allowFraction max={l.unitPrice * l.qty}
              onCancel={() => setKeypad(null)}
              onAccept={(n) => { setLineDiscount(keypad.lineId, n); setKeypad(null); }}
            />
          );
        }
        return (
          <NumericKeypad
            title={`Cantidad — ${l.name}`}
            initial={String(l.qty)}
            allowFraction={keypad.allowFraction}
            onCancel={() => setKeypad(null)}
            onAccept={(n) => { setLineQty(keypad.lineId, n); setKeypad(null); }}
          />
        );
      })()}

      {unitPicker && (
        <UnitPickerModal product={unitPicker}
          onCancel={() => setUnitPicker(null)}
          onPick={(idx) => { const p = unitPicker; setUnitPicker(null); addProduct(p, idx); }}
        />
      )}

      {showPark && <ParkModal onCancel={() => setShowPark(false)} onConfirm={(label) => parkSale(label)} />}

      {showQuote && <QuoteModal onCancel={() => setShowQuote(false)} onConfirm={(label, days) => quoteSale(label, days)} />}

      {showResume && (
        <ResumeModal
          sales={parkedQuery.data ?? []}
          onCancel={() => setShowResume(false)}
          onPick={resumeSale}
        />
      )}

      {showOpenItem && (
        <OpenItemModal
          onCancel={() => setShowOpenItem(false)}
          onConfirm={(d) => addOpenItem(d)}
        />
      )}

      {postSale && (
        <PostSaleModal
          data={{ total: postSale.total, change: postSale.change, defaultEmail: postSale.defaultEmail }}
          onPrint={async () => {
            const cashPaid = true; // si vino del cash, abre gaveta; el server no devuelve el detalle aquí
            await printReceipt(PRINTER, { content: postSale.receiptContent, openDrawer: cashPaid, cut: true });
          }}
          onSendEmail={async (email) => {
            // Email pendiente de Resend/SMTP: avisamos al cajero que la venta ya
            // se guardó (no se pierde) y queda lista para enviar cuando configures.
            try {
              const { supabase } = await import("../lib/supabase");
              const { data, error } = await supabase.functions.invoke("send-receipt", {
                body: { email, content: postSale.receiptContent, total: postSale.total },
              });
              if (error) throw error;
              const r = (data ?? {}) as { sent?: boolean; message?: string };
              return { ok: !!r.sent, message: r.message ?? (r.sent ? `Enviado a ${email}` : "Función send-receipt no responde aún (configura Resend).") };
            } catch (e) {
              return { ok: false, message: "Envío pendiente: configura la edge function send-receipt + Resend." };
            }
          }}
          onNewSale={() => {
            setPostSale(null);
            resetCart();
            setMsg(navigator.onLine ? "Venta registrada · sincronizando…" : "Venta guardada offline");
          }}
        />
      )}
    </div>
  );
}

// ---- OpenItemModal: alta de un ítem libre (servicio o producto fuera de catálogo) ----
function OpenItemModal({ onCancel, onConfirm }: {
  onCancel: () => void;
  onConfirm: (d: { description: string; unitPrice: number; qty: number; itbmsRate: string }) => void;
}) {
  const [description, setDescription] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [qty, setQty] = useState("1");
  const [itbmsRate, setItbmsRate] = useState("rate_7");
  const price = parseFloat(unitPrice) || 0;
  const q = parseFloat(qty) || 0;
  const ok = description.trim() && price > 0 && q > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <h2 className="mb-1 text-lg font-bold">Ítem libre</h2>
        <p className="mb-4 text-sm text-grafito-400">Para servicios o productos que no están en el catálogo (ej: "cortar tubo a 2.5m", "alquiler andamio").</p>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-grafito-500">Descripción</span>
          <input autoFocus value={description} onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand"
            placeholder="Ej: cortar tubo a 2.5m" />
        </label>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-grafito-500">Precio unitario (B/.)</span>
            <input type="number" inputMode="decimal" step="0.01" min="0"
              value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="0.00"
              className="w-full rounded-lg border px-3 py-2 text-right outline-none focus:border-brand" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-grafito-500">Cantidad</span>
            <input type="number" inputMode="decimal" step="0.01" min="0"
              value={qty} onChange={(e) => setQty(e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-right outline-none focus:border-brand" />
          </label>
        </div>
        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-grafito-500">ITBMS</span>
          <select value={itbmsRate} onChange={(e) => setItbmsRate(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand">
            <option value="rate_7">7% (estándar)</option>
            <option value="rate_10">10% (bebidas alcohólicas)</option>
            <option value="rate_15">15% (tabaco)</option>
            <option value="exento">Exento</option>
          </select>
        </label>
        <div className="mb-3 rounded-lg bg-grafito-50 px-3 py-2 text-sm">
          Importe: <strong>{money(price * q)}</strong> + ITBMS
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button onClick={() => ok && onConfirm({ description: description.trim(), unitPrice: price, qty: q, itbmsRate })}
            disabled={!ok} className="btn btn-primary flex-1 disabled:opacity-40">
            Agregar al carrito
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- ParkModal: pide etiqueta corta para identificar la venta suspendida ----
function ParkModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (label: string) => void }) {
  const [label, setLabel] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
        <h2 className="mb-2 text-lg font-bold">Suspender venta</h2>
        <p className="mb-3 text-sm text-grafito-400">Etiqueta para reconocerla cuando vuelvas a retomarla (ej: "Señor de azul", "Obra Juan").</p>
        <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Etiqueta"
          className="mb-4 w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button onClick={() => onConfirm(label.trim() || "sin etiqueta")} className="btn btn-primary flex-1">Suspender</button>
        </div>
      </div>
    </div>
  );
}

// ---- QuoteModal: cotización formal con vigencia en días -----------------
function QuoteModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (label: string, days: number) => void }) {
  const [label, setLabel] = useState("");
  const [days, setDays] = useState("15");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
        <h2 className="mb-2 text-lg font-bold">Convertir a cotización</h2>
        <p className="mb-3 text-sm text-grafito-400">No descuenta stock. La conviertes en venta cuando el cliente confirme (en "↪ Retomar").</p>
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-grafito-500">Referencia / cliente</span>
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder='Ej: "Constructora RG - obra Pedregal"'
            className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
        </label>
        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-grafito-500">Validez (días)</span>
          <input type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)}
            className="w-24 rounded-lg border px-3 py-2 outline-none focus:border-brand" />
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button onClick={() => onConfirm(label.trim() || "cotización", Math.max(1, parseInt(days) || 15))} className="btn btn-primary flex-1">Cotizar</button>
        </div>
      </div>
    </div>
  );
}

// ---- ResumeModal: lista de ventas parked/quote para retomar ---------------
function ResumeModal({ sales, onCancel, onPick }: {
  sales: { id: string; park_label: string | null; parked_at: string | null; total: number; status: string }[];
  onCancel: () => void;
  onPick: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <h2 className="mb-3 text-lg font-bold">Retomar venta</h2>
        {sales.length === 0 ? (
          <p className="text-sm text-grafito-400">No hay ventas suspendidas ni cotizaciones.</p>
        ) : (
          <div className="space-y-2">
            {sales.map((s) => (
              <button key={s.id} onClick={() => onPick(s.id)}
                className="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left hover:bg-grafito-50">
                <div>
                  <div className="font-semibold">{s.park_label ?? "(cotización)"}</div>
                  <div className="text-xs text-grafito-400">
                    {s.status === "quote" ? "Cotización" : "Suspendida"} · {s.parked_at ? new Date(s.parked_at).toLocaleTimeString("es-PA",{hour:"2-digit",minute:"2-digit"}) : ""}
                  </div>
                </div>
                <div className="font-bold text-brand">{money(Number(s.total))}</div>
              </button>
            ))}
          </div>
        )}
        <button onClick={onCancel} className="btn btn-ghost mt-4 w-full">Cerrar</button>
      </div>
    </div>
  );
}

function UnitPickerModal({ product, onPick, onCancel }: { product: CatalogItem; onPick: (idx: number) => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-1 text-sm text-grafito-400">Elegir presentación</div>
        <h2 className="mb-4 text-lg font-bold">{product.name}</h2>
        <div className="space-y-2">
          {product.units.map((u, i) => (
            <button key={u.product_unit_id} onClick={() => onPick(i)}
              className="flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left hover:bg-grafito-50 active:bg-grafito-100">
              <div>
                <div className="font-semibold">{u.unit_name} {u.factor !== 1 && <span className="text-grafito-400">· ×{u.factor}</span>}</div>
                <div className="text-xs text-grafito-400">{u.unit_code}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-brand">{u.price != null ? money(u.price) : "—"}</div>
                {u.factor !== 1 && u.price != null && (
                  <div className="text-[10px] text-grafito-400">≈ {money(u.price / u.factor)} c/u</div>
                )}
              </div>
            </button>
          ))}
        </div>
        <button onClick={onCancel} className="btn btn-ghost mt-4 w-full">Cancelar</button>
      </div>
    </div>
  );
}

// ---------- subcomponentes ---------------------------------------------------

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} type="button"
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold ${active ? "bg-brand text-white" : "bg-grafito-100 text-grafito-700 hover:bg-grafito-200"}`}>
      {children}
    </button>
  );
}

// Iconos por categoría — pista visual rápida para identificar el rubro sin leer.
const CATEGORY_ICON: Record<string, string> = {
  "Plomería": "🚿",
  "Eléctrico": "🔌",
  "Cemento y agregados": "🧱",
  "Herramientas": "🔧",
  "Fijaciones": "🔩",
  "Pintura": "🎨",
  "Iluminación": "💡",
};
function iconFor(name: string | null | undefined): string {
  return (name && CATEGORY_ICON[name]) || "📦";
}

function ProductTile({ p, onTap }: { p: CatalogItem; onTap: () => void }) {
  const u = p.units[0];
  const price = u?.price;
  const lowStock = p.qty_available <= 5;
  const outOfStock = p.qty_available <= 0;
  const multiUnit = p.units.length > 1;
  return (
    <button onClick={onTap} disabled={outOfStock}
      className="group flex h-36 flex-col justify-between rounded-xl border bg-white p-3 text-left shadow-sm transition active:scale-[0.97] active:bg-grafito-50 disabled:cursor-not-allowed disabled:opacity-50">
      <div className="flex items-start gap-2">
        <span className="text-2xl leading-none" aria-hidden>{iconFor(p.category_name)}</span>
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-sm font-semibold leading-tight text-grafito-900">{p.name}</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-grafito-400">{p.sku}</div>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-lg font-bold text-brand">{price != null ? money(price) : "—"}</div>
          <div className="flex items-center gap-1 text-[10px]">
            {multiUnit && <span className="rounded bg-grafito-200 px-1 py-0.5 font-semibold text-grafito-700">{p.units.length} pres.</span>}
            <span className={outOfStock ? "text-red-600 font-semibold" : lowStock ? "text-amber-700" : "text-grafito-400"}>
              {outOfStock ? "Sin stock" : `${p.qty_available} disp.`}
            </span>
          </div>
        </div>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand text-xl font-bold text-white shadow group-active:bg-brand/90">+</span>
      </div>
    </button>
  );
}

function CartLineRow({ line, computedTotal, onInc, onDec, onEditQty, onEditDiscount, onRemove }: {
  line: CartLine; computedTotal: number;
  onInc: () => void; onDec: () => void; onEditQty: () => void; onEditDiscount: () => void; onRemove: () => void;
}) {
  return (
    <div className="border-b px-3 py-2 last:border-0">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{line.name}</div>
          <div className="text-xs text-grafito-400">{money(line.unitPrice)} / {line.unitLabel}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onDec} className="h-9 w-9 rounded-lg bg-grafito-100 text-lg font-bold active:bg-grafito-200">−</button>
          <button onClick={onEditQty} className="min-w-[3rem] rounded-lg px-2 py-1 text-center font-bold tabular-nums hover:bg-grafito-50">{line.qty}</button>
          <button onClick={onInc} className="h-9 w-9 rounded-lg bg-grafito-100 text-lg font-bold active:bg-grafito-200">+</button>
        </div>
        <div className="w-20 text-right">
          <div className="font-semibold tabular-nums">{money(computedTotal)}</div>
          {line.discount > 0 && <div className="text-[10px] text-brand">−{money(line.discount)}</div>}
        </div>
        <button onClick={onRemove} className="text-grafito-300 hover:text-red-600">✕</button>
      </div>
      <div className="mt-1 flex items-center justify-end">
        <button onClick={onEditDiscount}
          className="text-[11px] font-semibold text-brand hover:underline">
          {line.discount > 0 ? `Desc. B/. ${line.discount.toFixed(2)} · editar` : "+ Desc."}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, muted, big }: { label: string; value: string; muted?: boolean; big?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between ${muted ? "text-sm text-grafito-400" : ""}`}>
      <span>{label}</span>
      <span className={big ? "text-3xl font-bold" : "tabular-nums"}>{value}</span>
    </div>
  );
}
